<template>
	<div
		class="height-full bg-neutral-25 pt-4 d-flex flex-column justify-space-between sidebar-container border-r-xs border-neutral-50"
		:style="{ flex: isCollapsed ? '' : '0 0 250px', height: '100vh' }"
		role="menu"
	>
		<div class="d-flex flex-column">
			<div class="pb-3 relative align-center px-4">
				<div
					:class="!isCollapsed ? 'gap-2' : 'pb-5'"
					class="d-flex pl-2"
					:style="{ marginBottom: isCollapsed ? '2px' : '' }"
				>
					<a
						@click.prevent="HomeNavigation.toHome()"
						class="cursor-pointer"
						:title="$t('web.trip.sidebar.go_back')"
					>
						<img :src="iconLogo" alt="Passporter" style="height: 18px" />
					</a>
					<div v-if="!isCollapsed">
						<p
							class="rounded-1 text-label-m trip-heading px-1 cursor-pointer truncated-2"
							@click="$emit('open-plan-trip')"
							style="max-height: 44px"
						>
							{{ trip.name }}
						</p>
						<div class="rounded-1 trip-heading px-1">
							<TripDashboardDates
								:trip="trip"
								:is-x-s="isXS"
								text-size="m"
								@change-dates="$emit('change-dates', $event)"
							/>
						</div>
					</div>
				</div>

				<button
					:title="isCollapsed ? $t('web.trip.sidebar.expand') : $t('web.trip.sidebar.close')"
					class="absolute right-0 top-0 cursor-pointer toggle-button bg-neutral-25 border-xs border-neutral-50 rounded-2 pa-2 z-5"
					@click="toggle"
				>
					<NewIcon
						icon="caret-left"
						radius="12px"
						:rotate="isCollapsed ? 'down' : ''"
						:title="isCollapsed ? $t('web.trip.sidebar.expand') : $t('web.trip.sidebar.close')"
					/>
				</button>
			</div>
			<MenuDivider />
			<div
				class="d-flex flex-column overflow-auto pb-2 flex-grow-1 overflow-y-auto vertical-scroll neutral px-4"
				style="min-height: 0"
			>
				<div class="d-flex width-full flex-column pt-4">
					<div class="d-flex flex-column gap-1">
						<TripMenuItem
							v-for="item in menuItems"
							:key="item.name"
							:name="$t(item.name)"
							:icon="item.icon"
							:is-active="isCurrentRoute(item.view!)"
							@click="item.action()"
							:is-collapsed="isCollapsed"
							:show-check="isXS"
							:is-new="item.isNew"
						/>
					</div>
					<Collapsable v-if="!isCollapsed" should-open separated-containers class="mt-4">
						<template #title>
							<span v-if="!isCollapsed" class="text-label-s text-neutral-800">
								{{ $t('web.trip.sidebar.store') }}
							</span>
						</template>
						<template #content>
							<div class="d-flex flex-column gap-1">
								<template v-for="item in storeMenuItems" :key="item.name">
									<TripMenuItem
										v-if="!item.hide"
										:name="$t(item.name)"
										:icon="item.icon"
										:is-active="isCurrentRoute(item.view!)"
										@click="item.action()"
										:is-collapsed="isCollapsed"
										:show-check="isXS"
										:is-new="item.isNew"
									/>
								</template>
							</div>
						</template>
					</Collapsable>
					<div v-else>
						<div class="d-flex flex-column gap-1">
							<div class="py-4" :style="{ marginBlock: isCollapsed ? '3px' : '' }">
								<MenuDivider />
							</div>
							<template v-for="item in storeMenuItems" :key="item.name">
								<TripMenuItem
									v-if="!item.hide"
									:name="$t(item.name)"
									:icon="item.icon"
									:is-active="isCurrentRoute(item.view!)"
									@click="item.action()"
									:is-collapsed="isCollapsed"
									:show-check="isXS"
									:is-new="item.isNew"
								/>
							</template>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div>
			<MenuDivider />
			<div class="d-flex px-4 py-1 gap-1" :class="isCollapsed ? 'flex-column' : ''">
				<template v-for="item in secondaryMenuItems" :key="item.view">
					<TripMenuItem
						v-if="!item.hide"
						:name="$t(item.name)"
						:icon="item.icon"
						:is-active="isCurrentRoute(item.view!)"
						@click="item.action()"
						:is-collapsed="true"
						:show-check="isXS"
					/>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { storeToRefs } from 'pinia'
	import { ref } from 'vue'
	import iconLogo from '~/assets/logo-icon.svg'
	import { HomeNavigation } from '~/services/navigation/home'
	import { useDeviceStore } from '~/store/device'
	import { Itinerary2 } from '~/models/Itinerary'
	import { useTripMenu } from '~/composables/tripMenu'
	import { MenuItem } from '~/composables/tripMenu'

	interface Props {
		trip: Itinerary2
		secondaryMenuItems: MenuItem[]
	}
	const props = defineProps<Props>()
	defineEmits(['open-invitation-dialog', 'open-plan-trip', 'open-request-help', 'change-dates'])

	const deviceStore = useDeviceStore()
	const { isXS } = storeToRefs(deviceStore)
	const { menuItems, storeMenuItems } = useTripMenu(props.trip.id!)
	const route = useRoute()

	const isCollapsed = ref<boolean>(false)

	const toggle = () => (isCollapsed.value = !isCollapsed.value)

	const isCurrentRoute = (itemRoute: string) => route.path.includes(`/${itemRoute}`)
</script>

<style scoped lang="scss">
	.separator {
		width: 100%;
		border: none;
		border-top: 2px solid var(--color-neutral-200);
	}
	.toggle-button {
		opacity: 0;
		transition: opacity 0.2s;
		transform: translateX(50%);
	}

	.sidebar-container:hover .toggle-button {
		opacity: 1;
	}
	.trip-heading {
		&:hover {
			background-color: var(--color-neutral-100);
		}
	}
</style>
